import {forEach, get, isEmpty, omit} from 'lodash'
import React, {FC, useEffect, useRef, useState} from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import {InputTextFormik, FormError} from '../../../../../_gori/partials/widgets'
import {OrdersConfig} from '../../../orders/core/_const'
import {OPTION_COUNTRIES} from '../../../../../_gori/constants'
import {fetchGeocode, searchAddress} from '../../utils'
import {useIntl} from 'react-intl'
import {FormikProps} from 'formik'
import clsx from 'clsx'

type Props = {
  required?: boolean
  label?: string | null
  formik: FormikProps<any>
  prefix: 'from' | 'to'
  className?: string
  labelClassName?: string
  disabled?: boolean
  placeholder?: string
  addressError: string
  setAddressError: (errorMessage: string) => void
}
const addOutsideClickListener = (ref, onOutsideClick) => {
  const handleWindowClick = (e) => {
    if (onOutsideClick) {
      let node = e.target
      while (node) {
        if (ref.current === node) {
          return
        }
        node = node.parentNode
      }
      onOutsideClick()
    }
  }
  window.addEventListener('click', handleWindowClick)
  return (): void => {
    window.removeEventListener('click', handleWindowClick)
  }
}

const useOutsideClickHandler = (onOutsideClick) => {
  const ref = React.useRef(null)
  React.useEffect(() => addOutsideClickListener(ref, onOutsideClick), [onOutsideClick])
  return ref
}

export const AutocompleteAddress: FC<Props> = ({
  required = true,
  label = null,
  formik,
  prefix = 'from',
  className = 'col-9',
  labelClassName = 'col-3 col-form-label',
  disabled = false,
  placeholder = '',
  addressError,
  setAddressError,
}) => {
  const [maps, setMaps] = useState<any>()
  const [show, setShow] = useState<boolean>(false)
  const {placesService, placePredictions, getPlacePredictions} = usePlacesService({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  })
  const intl = useIntl()
  const ref = useOutsideClickHandler(() => setShow(false))
  const selectedRef = useRef<boolean>(false)
  const timerIdRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    let list: any = []
    if (placePredictions.length > 0) {
      forEach(placePredictions, (item) => {
        list.push({
          value: item.place_id,
          mainText: item.structured_formatting?.main_text,
          secondaryText: item.structured_formatting?.secondary_text,
        })
      })
      setMaps(list)
      setShow(true)
    }
  }, [placePredictions])

  useEffect(() => {
    return () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current)
      }
    }
  }, [])

  const handleSelectPlace = async (placeDetails, fallbackText) => {
    selectedRef.current = true
    let {city, state, zip, country} = getAddressInfoFromSearching(placeDetails)
    if (!zip) {
      const lat = placeDetails.geometry?.location?.lat()
      const lng = placeDetails.geometry?.location?.lng()
      const centerPlace = await fetchGeocode({latlng: `${lat},${lng}`})
      if (centerPlace) {
        const centerAddressInfo = getAddressInfoFromSearching(centerPlace)
        zip = centerAddressInfo?.zip || zip
        city = centerAddressInfo?.city || city
        state = centerAddressInfo?.state || state
        country = centerAddressInfo?.country || country
      }
    }
    let checkExistsCountry
    if (country) {
      checkExistsCountry = OPTION_COUNTRIES.find((item) => item.value === country)
    }
    setOtherAddressValues({
      city,
      state,
      zip,
      country,
      checkExistsCountry,
      isSetValue: true,
    })

    const addressValue = zip && city && state
    if (!addressValue) {
      setInvalidAddress()
      return
    }
    clearError()
    formik.setFieldValue(`${prefix}.street1`, fallbackText)
  }
  const selectedPlace = (selected) => {
    setShow(false)
    placesService?.getDetails({placeId: selected.value}, (placeDetails) => {
      handleSelectPlace(placeDetails, selected.mainText)
    })
  }

  const getPlace = (e) => {
    selectedRef.current = false
    if (isEmpty(e?.target?.value)) {
      setMaps([])
      setShow(false)
    } else {
      const fields = ['city', 'state', 'country', 'zip']

      fields.forEach((key) => {
        const fullKey = `${prefix}.${key}`
        const currentValue = formik.values?.[prefix]?.[key]
        if (currentValue) {
          formik.setFieldValue(fullKey, '')
        }
      })
      getPlacePredictions({input: e?.target?.value})
    }
  }

  const handleBlur = async (e) => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current)
    }
    timerIdRef.current = setTimeout(async () => {
      if (selectedRef.current) return

      const value = e?.target?.value?.trim()
      if (!value) return

      const found = await searchAddress(value)
      if (!found) {
        setInvalidAddress()
        return
      }

      const addressInfo = getAddressInfoFromSearching(found)

      setOtherAddressValues({
        city: addressInfo?.city,
        state: addressInfo?.state,
        zip: addressInfo?.zip,
        country: addressInfo?.country,
      })
      const addressValue = addressInfo?.zip && addressInfo?.city && addressInfo?.state
      if (!addressValue) {
        setInvalidAddress()
      } else {
        clearError()
      }
    }, 300)
  }

  const setOtherAddressValues = ({
    city,
    state,
    zip,
    country,
    checkExistsCountry = true,
    isSetValue = false,
  }) => {
    Object.entries(omit(OrdersConfig.SHIPPING, ['RESIDENTIAL', 'VERIFIED'])).forEach(
      ([, value]: [string, any]) => {
        switch (value.value) {
          case OrdersConfig.SHIPPING.CITY.value:
            formik.setFieldValue(`${prefix}.city`, city)
            break
          case OrdersConfig.SHIPPING.STATE.value:
            formik.setFieldValue(`${prefix}.state`, state)
            break
          case OrdersConfig.SHIPPING.COUNTRY.value:
            formik.setFieldValue(`${prefix}.country`, checkExistsCountry ? country : '')
            break
          case OrdersConfig.SHIPPING.ZIP_CODE.value:
            formik.setFieldValue(`${prefix}.zip`, zip)
            break
        }
      }
    )
  }

  const setInvalidAddress = () => {
    setAddressError(intl.formatMessage({id: 'ORDER_INFO_ADDRESS_INVALID'}))
  }

  const clearError = () => {
    if (addressError) {
      setAddressError('')
    }
  }

  const getAddressInfoFromSearching = (data) => {
    const components = data.address_components

    const findComponent = (type: string) => components.find((c) => c.types.includes(type))

    const city = findComponent('locality')?.long_name || findComponent('neighborhood')?.long_name
    const state = findComponent('administrative_area_level_1')?.short_name
    const zip = findComponent('postal_code')?.long_name
    const country = findComponent('country').short_name

    return {city, state, zip, country}
  }
  return (
    <div className='flex-fill' ref={ref}>
      <div className='d-flex'>
        <InputTextFormik
          className={className}
          labelClassName={labelClassName}
          required={required}
          label={label}
          formik={formik}
          name={`${prefix}.street1`}
          onChange={(e) => getPlace(e)}
          onFocus={(e) => getPlace(e)}
          disabled={disabled}
          placeholder={placeholder}
          onBlur={handleBlur}
          checkFormik={false}
          configAppend={{
            name: '',
            position: 'right',
            classInput: clsx({
              'is-invalid': !!addressError || !!get(formik, `errors.${prefix}.street1`),
            }),
          }}
        />
      </div>
      {!!addressError && !get(formik, `errors.${prefix}.street1`) && (
        <FormError message={addressError} />
      )}
      <div className='input-group'>
        <div data-bs-toggle='dropdown' aria-expanded={show}></div>
        <div className={`dropdown-menu fw-bold fs-7 py-2 ${show ? 'show' : ''} w-100`}>
          {!isEmpty(maps) &&
            maps.map((item: any, index: number) => {
              return (
                <div
                  className={`map-item my-1 text-gray-600 text-hover-dark cursor-pointer fw-bold`}
                  key={index}
                  onClick={() => {
                    selectedPlace(item)
                  }}
                >
                  <span className='menu-link'>
                    {item.mainText}
                    {!isEmpty(item.secondaryText) ? ' , ' : ''}
                    <span style={{color: 'rgb(123, 153, 168)'}}>{item.secondaryText}</span>
                  </span>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
